<template>
  <el-container>
    <el-main>
      <div class="home">
        <el-card class="ta-main" v-if="!dataLoaded">
          <div class="ta-header ta-header__main">
            {{$t('statistics.codeLabel')}}
          </div>
          <el-row class="ta-row">
            <el-col :span="20" class="ta-label">
              <el-input v-model="code" class="ta-input"/>
            </el-col>
            <el-col :span="4">
              <el-button @click="getStatistics">
                {{$t('statistics.btnText')}}
              </el-button>
            </el-col>
          </el-row>
          <el-row v-if="error">
            <el-col :span="20" class="ta-label ta-label__error">
              {{error}}
            </el-col>
          </el-row>
        </el-card>

        <el-card class="ta-main" v-else>

          <div class="ta-header ta-header__main">
            {{$t('statistics.title')}}
          </div>

          <el-row class="ta-row">
            <el-col :span="6" class="ta-label ta-label__bold">
              {{$t('statistics.countryTitle')}}
            </el-col>
            <el-col :span="9" class="ta-label ta-label__bold ta-label__centered">
              {{$t('statistics.numberOfUniqueRunsTitle')}}
            </el-col>
            <el-col :span="9" class="ta-label ta-label__bold ta-label__centered">
              {{$t('statistics.numberOfRunsTitle')}}
            </el-col>
          </el-row>

          <el-row class="ta-row" v-for="row in countryData" :key="row.country">
            <el-col :span="6" class="ta-label">
              {{row.country}}
            </el-col>
            <el-col :span="9">
              {{row.uniqueRuns}}
            </el-col>
            <el-col :span="9">
              {{row.runs}}
            </el-col>
          </el-row>

          <el-row class="ta-header">
            <div class="ta-chart-header ta-chart-header__with-hint">
              {{$t('statistics.chartTitle')}}
            </div>
            <div>
              <div id="stats_chart"/>
            </div>
          </el-row>

        </el-card>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import axios from 'axios'
import I18nMixin from '../i18n/i18n_mixin'
import Highcharts from 'highcharts'

export default {
  name: 'Statistics',
  mixins: [I18nMixin],
  data () {
    return {
      id: 'stats_chart',
      data: [],
      code: null,
      dataLoaded: false,
      error: null
    }
  },
  computed: {
    input () {
      return this.data.userInput || {}
    },
    ipData () {
      const result = {}
      for (const row of this.data) {
        if (result[row.ip]) {
          result[row.ip].count += 1
        } else {
          result[row.ip] = {
            count: 1,
            country: row.country
          }
        }
      }
      return Object.values(result)
    },
    countryData () {
      const result = {}
      for (const row of this.ipData) {
        if (result[row.country]) {
          result[row.country].uniqueRuns += 1
          result[row.country].runs += row.count
        } else {
          result[row.country] = {
            uniqueRuns: 1,
            runs: row.count,
            country: row.country
          }
        }
      }
      return Object.values(result)
    }
  },
  methods: {
    getStatistics () {
      this.error = null
      axios.post('/api/v1/statistics', this.code, { headers: { 'Content-Type': 'text/plain' } })
        .then(r => r.data)
        .then(d => {
          this.data = d
          this.dataLoaded = true
          this.$nextTick(() => this.initChart())
        })
        .catch(() => {
          this.error = this.$t('statistics.loadingError')
        })
    },
    initChart () {
      Highcharts.chart(this.id, {
        chart: {
          type: 'pie',
          backgroundColor: '#F1F6E4'
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f} %'
            },
            showInLegend: true
          }
        },
        title: {
          text: ''
        },
        series: this.getSeries()
      })
    },
    getSeries () {
      const values = []
      const series = []
      for (const row of this.countryData) {
        values.push({
          name: row.country,
          y: row.uniqueRuns
        })
      }
      series.push({
        name: this.$t('statistics.numberOfUniqueRunsTitle'),
        colorByPoint: true,
        data: values
      })
      return series
    }
  },
  mounted () {
    const locale = this.$route.query.lang
    if (this.isLocaleSupported(locale)) {
      this.setLocale(locale)
    }
  }
}
</script>

<style lang="scss">
  .home {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    flex-wrap: wrap;
    align-content: stretch;

    .ta-main {
      background: #F1F6E4;
      margin: 10pt;
      max-width: 800px;
      flex-basis: 600px;
      flex-grow: 1;

      @media all and (max-width: 500px) {
        margin-left: 0;
        margin-right: 0;
      }

      .ta-row {
        padding: 5px 20px;
        display: flex;
        flex-direction: row;
        align-items: center;
        vertical-align: middle;

        &:nth-of-type(even) {
          background: #E0F2D9;
        }

        &:nth-of-type(odd) {
          background: #F1F6E4;
        }

        &__inner {
          padding-left: 0;
          padding-right: 0;
        }

        .ta-input {
          width: 100%;
        }
      }

      .ta-row-block {
        padding: 5px 20px;
        background: none;
        display: flex;
        flex-direction: row;
        align-items: center;
        vertical-align: middle;

        & + .ta-row-block {
          border-top: 1px solid;
        }
      }
    }
  }

  .ta-label {
    text-align: start;

    &__bold {
      font-weight: bold;
    }

    &__error {
      color: red;
      font-weight: bold;
    }

    &__centered {
      display: flex;
      justify-content: center;
    }
  }

  .ta-header {
    margin-top: 10pt;
    padding-bottom: 20px;
    font-weight: bold;
    font-size: large;

    &__main {
      font-size: x-large;
    }
  }

  .ta-footer {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
  }

  .ta-chart-header {
    display: flex;
    justify-content: center;
    font-size: 15pt;
    padding: 10pt;

    &__with-hint {
      padding-bottom: 3pt;
    }

    &__small {
      padding-top: 0;
      padding-bottom: 10pt;
      font-size: 10pt;
      font-weight: lighter;
    }
  }

</style>
